import { useState } from 'react';
import { MapPin, PencilSimple, Trash, Check, X, Plus } from '@phosphor-icons/react';

// Predefined trip colors
const TRIP_COLORS = [
  '#3A5F82', // Blue
  '#5D9085', // Teal
  '#7C9981', // Sage
  '#8B7FAF', // Purple
  '#A88F87', // Mauve
  '#C4786E', // Coral
  '#D4A574', // Sand
  '#6B8E4E', // Forest
];

/**
 * TripsCard - Manage trips in Settings
 */
export default function TripsCard({ 
  trips = [], 
  onUpdateTrip, 
  onDeleteTrip,
  onCreateTrip,
  loading = false,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(TRIP_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  function startEdit(trip) {
    setEditingId(trip.id);
    setEditName(trip.name);
    setEditColor(trip.color || TRIP_COLORS[0]);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditColor('');
  }

  async function saveEdit() {
    if (!editName.trim() || saving) return;
    
    setSaving(true);
    try {
      await onUpdateTrip(editingId, { name: editName.trim(), color: editColor });
      cancelEdit();
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate() {
    if (!newName.trim() || saving) return;
    
    setSaving(true);
    try {
      await onCreateTrip({ name: newName.trim(), color: newColor });
      setShowCreate(false);
      setNewName('');
      setNewColor(TRIP_COLORS[0]);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteConfirm || saving) return;
    
    setSaving(true);
    try {
      await onDeleteTrip(deleteConfirm);
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={18} weight="duotone" style={{ color: 'var(--color-brand-primary)' }} />
          <span>Trips</span>
        </div>
        {!showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
            style={{ 
              color: 'var(--color-brand-primary)',
              backgroundColor: 'var(--color-brand-primary-soft)',
            }}
          >
            <Plus size={14} weight="bold" />
            New
          </button>
        )}
      </div>
      <div className="card-body">
        {/* Create new trip form */}
        {showCreate && (
          <div 
            className="mb-4 p-3 rounded-lg"
            style={{ backgroundColor: 'var(--color-bg-surface)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Trip name..."
                className="flex-1 px-2 py-1.5 text-sm rounded"
                style={{
                  backgroundColor: 'var(--color-bg-canvas)',
                  border: '1px solid var(--color-border-subtle)',
                  color: 'var(--color-text-primary)',
                }}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Color:
              </span>
              <div className="flex gap-1">
                {TRIP_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setNewColor(color)}
                    className="w-5 h-5 rounded-full transition-transform"
                    style={{ 
                      backgroundColor: color,
                      transform: newColor === color ? 'scale(1.2)' : 'scale(1)',
                      outline: newColor === color ? '2px solid var(--color-brand-primary)' : 'none',
                      outlineOffset: '2px',
                    }}
                    aria-label={`Select color ${color}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setNewName('');
                }}
                className="px-3 py-1 text-sm rounded"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || saving}
                className="px-3 py-1 text-sm rounded disabled:opacity-50"
                style={{ 
                  backgroundColor: 'var(--color-brand-primary)',
                  color: 'white',
                }}
              >
                Create
              </button>
            </div>
          </div>
        )}

        {/* Trip list */}
        {loading ? (
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Loading trips...
          </p>
        ) : trips.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            No trips created yet. Create a trip to group your events!
          </p>
        ) : (
          <div className="space-y-2">
            {trips.map(trip => (
              <div 
                key={trip.id}
                className="flex items-center gap-3 p-2 rounded-lg"
                style={{ backgroundColor: 'var(--color-bg-surface)' }}
              >
                {editingId === trip.id ? (
                  // Edit mode
                  <>
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: editColor }}
                    />
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-2 py-1 text-sm rounded"
                      style={{
                        backgroundColor: 'var(--color-bg-canvas)',
                        border: '1px solid var(--color-border-subtle)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                    <div className="flex gap-1">
                      {TRIP_COLORS.map(color => (
                        <button
                          key={color}
                          onClick={() => setEditColor(color)}
                          className="w-4 h-4 rounded-full"
                          style={{ 
                            backgroundColor: color,
                            outline: editColor === color ? '2px solid var(--color-brand-primary)' : 'none',
                            outlineOffset: '1px',
                          }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={saveEdit}
                      disabled={!editName.trim() || saving}
                      className="p-1 rounded disabled:opacity-50"
                      style={{ color: 'var(--color-status-success)' }}
                    >
                      <Check size={18} weight="bold" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-1 rounded"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <X size={18} />
                    </button>
                  </>
                ) : deleteConfirm === trip.id ? (
                  // Delete confirmation
                  <>
                    <span 
                      className="flex-1 text-sm"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      Delete "{trip.name}"?
                    </span>
                    <button
                      onClick={confirmDelete}
                      disabled={saving}
                      className="px-2 py-1 text-xs rounded disabled:opacity-50"
                      style={{ 
                        backgroundColor: 'var(--color-status-error)',
                        color: 'white',
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1 text-xs rounded"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  // View mode
                  <>
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: trip.color || '#3A5F82' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-sm font-medium truncate"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {trip.name}
                      </p>
                      <p 
                        className="text-xs"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {trip.event_count || 0} event{trip.event_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => startEdit(trip)}
                      className="p-1.5 rounded transition-colors hover:bg-[var(--color-bg-raised)]"
                      style={{ color: 'var(--color-text-muted)' }}
                      aria-label="Edit trip"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(trip.id)}
                      className="p-1.5 rounded transition-colors hover:bg-[var(--color-bg-raised)]"
                      style={{ color: 'var(--color-text-muted)' }}
                      aria-label="Delete trip"
                    >
                      <Trash size={16} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
